import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';

const fetchUtils = new FetchUtils();

export interface LabelCatalogueEntry {
	// Omit for a label the backend should create.
	id?: number;
	label: string;
	color?: string;
}

// PUT {path}/sync with the tenant's entire desired label catalogue in one request, replacing the
// per-label create/update/delete calls the editors used to fire in parallel.
//
// The payload IS the desired state: the backend creates entries without an id, updates the ones
// that carry an id, and DELETES every label it holds that the payload omits (an empty array is a
// legal request that clears the catalogue). Callers must therefore always send the complete
// catalogue — never a partial list, and never a list captured before it finished loading.
//
// Rejections to expect: an id the tenant does not own, duplicate names within the payload, or a
// name that collides with a label kept under a different id. This rejects rather than raising a
// Ids the sync will delete: the payload IS the desired catalogue, so anything the store held
// pre-save and the payload omits is on its way out. Entries without an id are new creates.
export const getDeletedLabelIds = (
	catalogueBeforeSync: any[],
	payload: LabelCatalogueEntry[]
): number[] => {
	const keptIds = new Set(
		(payload || [])
			.filter((entry) => entry.id !== undefined && entry.id !== null)
			.map((entry) => Number(entry.id))
	);
	return (catalogueBeforeSync || [])
		.map((label) => Number(label.id))
		.filter((id) => !Number.isNaN(id) && !keptIds.has(id));
};

// Lets the sync tell its module which labels it just deleted, so the module can drop them from
// the cached entities that carry a denormalized copy. A refetch cannot do this on its own.
export interface LabelSyncPurgeTarget {
	// Catalogue as it stood before this sync, used to diff which labels are being deleted.
	getCatalogue: (state: any) => any[];
	// Thunk that strips those label ids from this module's cached records.
	onDeletedLabels: (deletedLabelIds: number[]) => any;
}

export const createLabelCatalogueSyncThunk =
	(buildPath: (roleType: string) => string, errorMessage: string, purge?: LabelSyncPurgeTarget) =>
	(roleType: string) =>
	(labels: LabelCatalogueEntry[]) =>
	(dispatch, getState?) =>
		new Promise<any>((resolve, reject) => {
			const catalogueBeforeSync = purge && getState ? purge.getCatalogue(getState()) : [];
			fetchUtils
				.put(`${getBackendUri()}${buildPath(roleType)}`, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: labels,
				})
				.then(parseJSON)
				.then((result) => {
					const status = result.status;
					const ok = result.ok;
					const json = result.json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (!ok || (json && json.status === 'error')) {
						reject(json && json.message ? json.message : errorMessage);
					} else {
						if (purge) {
							const deletedLabelIds = getDeletedLabelIds(catalogueBeforeSync, labels);
							if (deletedLabelIds.length) {
								dispatch(purge.onDeletedLabels(deletedLabelIds));
							}
						}
						resolve(json && json.data !== undefined ? json.data : json);
					}
				})
				.catch((error) => {
					reject(error && error.message ? error.message : errorMessage);
				});
		});
